import { Table, TableColumn } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import React, { FC, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { SearchArticlesItem } from '../../common/api/searchArticles';
import { Loader } from '../../common/components/Loader';
import { usePageEngagement } from '../../common/hooks/usePageEngagement';
import { SEARCH_LIMIT } from '../../common/reducers/solutions';
import {
  selectSolutions,
  selectSolutionsPaging,
} from '../../common/selectors/solutions';
import { renderArticlesListItem } from '../../help-center/components/ArticlesListItem';
import { Subtitle } from '../../help-center/components/Subtitle';
import { TLocalesKeys } from '../../types';
import { biHelpCenter } from '../../util/bi';
import { SolutionsNotReady } from './SolutionNotReady';
import { useFetchSolutions } from './useFetchSolutions';

const columns: TableColumn<SearchArticlesItem>[] = [
  {
    title: '', // because of titleBarVisible={false}
    style: { padding: 0, boxShadow: 'none' },
    render: renderArticlesListItem,
  },
];

export const SolutionList: FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const containerRef = useRef(null);
  const { onScroll, onScrollAddedContent } = usePageEngagement();
  const solutions = useSelector(selectSolutions);
  const paging = useSelector(selectSolutionsPaging);

  const query = searchParams.get('query')!;
  const { fetchSolutions } = useFetchSolutions(query);

  const loadMore = React.useCallback(() => {
    onScrollAddedContent();
    fetchSolutions();
  }, [fetchSolutions, onScrollAddedContent]);

  const sendBackBi = useCallback(
    () => biHelpCenter.logBackButtonOnArticlesListView(solutions!),
    [solutions],
  );
  const solutionNotReady = (
    <SolutionsNotReady solutions={solutions} query={query} />
  );
  return (
    <>
      <Subtitle
        text={t<TLocalesKeys>('help-center-search-result')}
        sendBackBi={sendBackBi}
      />
      <div
        ref={containerRef}
        style={{ height: '100%', maxHeight: '100%', overflowY: 'scroll' }}
        data-hook="solution-list"
        onScroll={onScroll}
      >
        {solutionNotReady}
        {solutions && solutions.length > 0 && (
          <Table
            infiniteScroll
            hasMore={paging.hasMore}
            loadMore={loadMore}
            itemsPerPage={SEARCH_LIMIT}
            data={solutions}
            columns={columns}
            scrollElement={containerRef.current!}
            loader={<Loader dataHook="solution-list__loader" margin="25px" />}
          >
            <Table.Content titleBarVisible={false} />
          </Table>
        )}
      </div>
    </>
  );
};
